import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Avatar,
  Text,
  useColorModeValue,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Spinner,
  useToast,
  Image,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfile } from '@/contexts/userprofile/UserProfileContext';
import { useThemeContext } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { CachedAvatar } from '@/components/common/CachedAvatar';
import { useApi } from '@/contexts/ApiContext';

// Custom SVG Icons
const HamburgerIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M3 6h18v2H3V6m0 5h18v2H3v-2m0 5h18v2H3v-2Z"
    />
  </Icon>
);

const MoonIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M17.75 4.09L15.22 6.03L16.13 9.09L13.5 7.28L10.87 9.09L11.78 6.03L9.25 4.09L12.44 4L13.5 1L14.56 4L17.75 4.09M21.25 11L19.61 12.25L20.2 14.23L18.5 13.06L16.8 14.23L17.39 12.25L15.75 11L17.81 10.95L18.5 9L19.19 10.95L21.25 11M18.97 15.95C19.8 15.87 20.69 17.05 20.16 17.8C19.84 18.25 19.5 18.67 19.08 19.07C15.17 23 8.84 23 4.94 19.07C1.03 15.17 1.03 8.83 4.94 4.93C5.34 4.53 5.76 4.17 6.21 3.85C6.96 3.32 8.14 4.21 8.06 5.04C7.79 7.9 8.75 10.87 10.95 13.06C13.14 15.26 16.1 16.22 18.97 15.95Z"
    />
  </Icon>
);

const SunIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6m8 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0M6 12a1 1 0 1 1-2 0 1 1 0 0 1 2 0m6-8a1 1 0 1 1 0-2 1 1 0 0 1 0 2m0 16a1 1 0 1 1 0-2 1 1 0 0 1 0 2m9-11a1 1 0 1 1-2 0 1 1 0 0 1 2 0M7 7a1 1 0 1 1-2 0 1 1 0 0 1 2 0m10 10a1 1 0 1 1-2 0 1 1 0 0 1 2 0M7 17a1 1 0 1 1-2 0 1 1 0 0 1 2 0"
    />
  </Icon>
);

const SettingsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5a3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97c0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1c0 .33.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z"
    />
  </Icon>
);

const MenuIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M4 6h16v2H4V6m0 5h16v2H4v-2m0 5h16v2H4v-2Z"
    />
  </Icon>
);

const QRCodeIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M3 11V3h8v8H3Zm2-2h4V5H5v4Zm8-6v8h8V3h-8Zm6 6h-4V5h4v4ZM3 21v-8h8v8H3Zm2-2h4v-4H5v4Zm10-2h2v2h-2v-2Zm2-2h2v2h-2v-2Zm-2 0h2v-2h-2v2Zm0 4h2v2h-2v-2Zm2 0h2v-2h-2v2Zm0-8h2v2h-2v-2Zm2 2h2v2h-2v-2Z"
    />
  </Icon>
);

const ChevronDownIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={4}>
    <path
      fill="currentColor"
      d="M7.41 8.58L12 13.17l4.59-4.59L18 10l-6 6l-6-6l1.41-1.42Z"
    />
  </Icon>
);

const ProfileIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 4a4 4 0 0 1 4 4a4 4 0 0 1-4 4a4 4 0 0 1-4-4a4 4 0 0 1 4-4m0 10c4.42 0 8 1.79 8 4v2H4v-2c0-2.21 3.58-4 8-4Z"
    />
  </Icon>
);

const LogoutIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M16 17v-3H9v-4h7V7l5 5l-5 5M14 2a2 2 0 0 1 2 2v2h-2V4H14H4v16h10v-2h2v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10Z"
    />
  </Icon>
);

interface NavbarProps {
  onLeftMenuToggle: () => void;
  onRightMenuToggle: () => void;
  isLeftMenuOpen: boolean;
  isRightMenuOpen: boolean;
}

export const Navbar: React.FC<NavbarProps> = ({
  onLeftMenuToggle,
  onRightMenuToggle,
  isLeftMenuOpen,
  isRightMenuOpen,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { profile } = useUserProfile();
  const { colorMode, toggleColorMode } = useThemeContext();
  const api = useApi();
  const navigate = useNavigate();
  const toast = useToast();
  const { isOpen: isQROpen, onOpen: onQROpen, onClose: onQRClose } = useDisclosure();
  
  // State for user avatar and QR code
  const [activeAvatar, setActiveAvatar] = useState<any>(null);
  const [userProfileId, setUserProfileId] = useState<string>('');
  const [qrCodeUrl, setQRCodeUrl] = useState<string>('');
  const [isLoadingQR, setIsLoadingQR] = useState(false);
  const [qrError, setQrError] = useState<string | null>(null);

  const bgColor = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');

  // Set user profile ID from profile data
  useEffect(() => {
    if (profile?.id) {
      setUserProfileId(profile.id);
    }
  }, [profile?.id]);

  const handleSignOut = async () => {
    logout();
  };

  const handleQRCodeClick = async () => {
    onQROpen();
    await generateQRCode();
  };

  const generateQRCode = async () => {
    try {
      setIsLoadingQR(true);
      setQrError(null);

      // Вызываем API для получения QR кода с правильными настройками для бинарных данных
      const response = await api.AuthApi.generateTokensQRCode({
        responseType: 'blob' // Указываем что ожидаем бинарные данные
      });
      
      // Создаем blob URL из бинарных данных
      const blob = new Blob([response.data], { type: 'image/png' });
      const qrUrl = URL.createObjectURL(blob);
      setQRCodeUrl(qrUrl);

    } catch (error) {
      console.error('Failed to generate QR code:', error);
      setQrError(t('nav.qrGenerationError'));
      toast({
        title: t('nav.qrGenerationError'),
        description: t('nav.qrGenerationErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoadingQR(false);
    }
  };

  const handleQRModalClose = () => {
    onQRClose();
    // Clean up the blob URL
    if (qrCodeUrl) {
      URL.revokeObjectURL(qrCodeUrl);
      setQRCodeUrl('');
    }
    setQrError(null);
  };

  return (
    <>
      <Box
        bg={bgColor}
        borderBottom="1px"
        borderColor={borderColor}
        px={4}
        h="60px"
        position="sticky"
        top={0}
        zIndex={1000}
      >
        <Flex h="60px" alignItems="center" justifyContent="space-between">
          {/* Left side - menu toggles and logo */}
          <HStack spacing={4}>
            <IconButton
              aria-label={t('sidebar.toggleLeft')}
              icon={<HamburgerIcon />}
              variant="ghost"
              onClick={onLeftMenuToggle}
              colorScheme={isLeftMenuOpen ? 'primary' : 'gray'}
            />
            
            <Text fontSize="xl" fontWeight="bold" color="primary.500">
              Corde
            </Text>
          </HStack>

          {/* Right side - controls and user menu */}
          <HStack spacing={4}>
            {/* Theme toggle */}
            <ThemeToggle size="sm" variant="ghost" />

            {/* Right menu toggle */}
            <IconButton
              aria-label={t('sidebar.toggleRight')}
              icon={<MenuIcon />}
              variant="ghost"
              onClick={onRightMenuToggle}
              colorScheme={isRightMenuOpen ? 'primary' : 'gray'}
            />

            {/* User menu */}
            {isAuthenticated && (
              <Menu>
                <MenuButton as={Button} variant="ghost" rightIcon={<ChevronDownIcon />}>
                  <HStack spacing={2}>
                    {activeAvatar && userProfileId ? (
                      <CachedAvatar 
                        size="sm" 
                        avatar={activeAvatar} 
                        profileId={userProfileId}
                        name={profile?.display_name || profile?.first_name || 'User'}
                      />
                    ) : (
                      <Avatar 
                        size="sm" 
                        name={profile?.display_name || profile?.first_name || 'User'}
                      />
                    )}
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={handleQRCodeClick}>
                    <HStack spacing={2}>
                      <QRCodeIcon />
                      <Text>{t('nav.qrLogin')}</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem onClick={() => navigate('/profile')}>
                    <HStack spacing={2}>
                      <ProfileIcon />
                      <Text>{t('nav.profile')}</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem onClick={() => navigate('/settings')}>
                    <HStack spacing={2}>
                      <SettingsIcon />
                      <Text>{t('nav.settings')}</Text>
                    </HStack>
                  </MenuItem>
                  <MenuItem onClick={handleSignOut}>
                    <HStack spacing={2}>
                      <LogoutIcon />
                      <Text>{t('nav.logout')}</Text>
                    </HStack>
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* QR Code Modal */}
      <Modal isOpen={isQROpen} onClose={handleQRModalClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('nav.qrCodeTitle')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center" py={6}>
            {isLoadingQR ? (
              <VStack spacing={4}>
                <Spinner size="xl" color="primary.500" />
                <Text color="text.muted">{t('nav.qrGenerating')}</Text>
              </VStack>
            ) : qrError ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('nav.qrGenerationError')}</AlertTitle>
                  <AlertDescription>{qrError}</AlertDescription>
                </Box>
              </Alert>
            ) : qrCodeUrl ? (
              <VStack spacing={4}>
                <Image 
                  src={qrCodeUrl} 
                  alt="QR Code for login" 
                  maxW="300px" 
                  mx="auto"
                  borderRadius="md"
                />
                <Text fontSize="sm" color="text.muted">
                  {t('nav.qrCodeDescription')}
                </Text>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <QRCodeIcon />
                <Text color="text.muted">{t('nav.qrLoadingError')}</Text>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="primary" 
              mr={3} 
              onClick={handleQRModalClose}
            >
              {t('common.close')}
            </Button>
            {qrError && (
              <Button 
                variant="outline" 
                onClick={generateQRCode}
                isLoading={isLoadingQR}
              >
                {t('nav.qrRetry')}
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}; 